// composables/useCatalogData.js
import { ref, computed, watch } from 'vue'
import { useRequest } from '@/composables/useRequest'

// Extracted so a second, independent `useAsyncData` call (e.g. an SSR
// priming fetch keyed to match this one, run from a component that renders
// before this composable's caller) can share the exact same fetch/redirect
// logic instead of drifting out of sync with it.
export async function fetchCatalogPayload(url) {
  if (!url) return null

  const route = useRoute()
  const res = await useRequest(url)

  if (!res.success) {
    throw new Error('API returned success: false')
  }

  if (res?.redirect && res?.redirect.redirect_url) {
    const { redirect_url, redirect_status } = res.redirect
    const currentPath = route.fullPath
    const isSamePage =
      redirect_url === currentPath ||
      (import.meta.client &&
        redirect_url === `${window.location.origin}${currentPath}`)

    if (!isSamePage) {
      await navigateTo(redirect_url, {
        redirectCode: parseInt(redirect_status) || 301,
        external: redirect_url.startsWith('http'),
      })
      return null
    }

    console.warn('Recursive redirect detected and blocked:', redirect_url)
  }

  return res
}

// Page.vue primes a catalog fetch (same key: `catalog-${type}:${apiUrl}`)
// before BlockCatalog mounts, purely to resolve catalog meta early for SSR
// ordering (see the comment there). Because both call sites share the same
// key, `nuxtApp._asyncData[key]` — and its `execute`/options closure — is
// created once by *whichever call runs first* and simply reused by the
// other; passing a custom `getCachedData` to only one of the two
// `useAsyncData` calls has no effect on the other's `execute()`, since that
// reuses the first caller's closure. So this needs to be passed to *both*
// call sites for either one to actually see it. `_init` gates the reuse to
// a still-live entry, so a torn-down entry left over from a previously
// selected filter combo (see the key comment in useCatalogData below) is
// never served as if it were fresh.
export function getCatalogCachedData(key, nuxtApp, ctx) {
  if (nuxtApp.isHydrating) return nuxtApp.payload.data[key]
  const existing = nuxtApp._asyncData[key]
  if (
    existing?._init &&
    existing.status.value === 'success' &&
    existing.data.value != null
  ) {
    return existing.data.value
  }
  if (ctx.cause !== 'refresh:manual' && ctx.cause !== 'refresh:hook') {
    return nuxtApp.static.data[key]
  }
}

export function useCatalogData(apiUrl, mobilePreviewUrl = null, cacheKey = 'catalog') {
  // useAsyncData registers this fetch with Nuxt's SSR payload/async-data
  // system, so `renderToString` waits for it to resolve before the HTML
  // is serialized. A plain `watch(apiUrl, asyncFn)` (the old approach)
  // is invisible to Nuxt's SSR renderer — it fires the promise but the
  // server never waits for it, so the server-rendered (and no-JS) markup
  // always shipped with empty data.
  const {
    data: rawCatalogPayload,
    pending: loading,
    error,
    refresh: refreshCatalog,
  } = useAsyncData(
    // Must stay unique per apiUrl, not just per catalog type: Nuxt caches
    // useAsyncData results by key across remounts. Browser back/forward
    // triggers a page remount (NuxtPage keys pages by route path), and a
    // key scoped only to `type` would resolve to the same cache entry as
    // before the navigation — reusing stale data instead of refetching.
    () => `${cacheKey}:${apiUrl.value}`,
    () => fetchCatalogPayload(apiUrl.value),
    {
      // No `watch` option needed: the key above already embeds apiUrl, so
      // Nuxt's own key-change handling triggers the refetch. Adding an
      // explicit watch here would double-fire — once for the new key
      // (correct) and once more against the original mount-time key's
      // stale asyncData object (wasted request to the old URL).
      default: () => null,
      // See getCatalogCachedData above: must be passed here too, not just
      // from Page.vue's priming call, since either one could be the call
      // that actually creates nuxtApp._asyncData[key] first.
      getCachedData: getCatalogCachedData,
    },
  )

  // The async data key is unique per apiUrl (see comment above), so every
  // filter change points `rawCatalogPayload` at a brand-new cache entry
  // that starts out `null` until its fetch resolves. Deriving the display
  // straight from that would blank the whole catalog (and, worse, collapse
  // `isFiltersUpdated`/`meta`-gated UI like the transport tabs) on every
  // single request. `catalogPayload` prefers the live `rawCatalogPayload`
  // and only falls back to the last resolved one while a new fetch is in
  // flight, so the previous results stay visible — with the loading
  // overlay on top — until the new ones arrive.
  //
  // This used to be a plain `ref` synced via `watch(rawCatalogPayload, ...)`.
  // That watch callback is not guaranteed to run again during SSR after
  // Nuxt's onServerPrefetch resolves `rawCatalogPayload` — confirmed by
  // instrumenting it directly: it never fired a second time, leaving
  // `catalogPayload` (and everything derived from it: requestData, meta,
  // the SEO title/description) stuck at their initial `null` for the
  // entire server render. Making `catalogPayload` a computed that reads
  // `rawCatalogPayload.value` directly removes that dependency on the
  // watch having fired — computeds re-evaluate lazily from current state
  // on every read, so the final render (and useSeoMeta's reactive tags)
  // always sees the resolved data regardless of watcher timing.
  const lastResolvedPayload = ref(null)
  watch(rawCatalogPayload, (payload) => {
    if (payload != null) {
      lastResolvedPayload.value = payload
    }
  })
  const catalogPayload = computed(
    () => rawCatalogPayload.value ?? lastResolvedPayload.value,
  )

  const requestData = computed(() => catalogPayload.value?.data ?? [])
  const meta = computed(() => catalogPayload.value?.meta ?? null)
  const forFilters = computed(() => catalogPayload.value?.for_filters ?? null)
  const allFilterValues = computed(
    () => catalogPayload.value?.all_filter_values ?? null,
  )

  // Mobile filter preview stays client-driven (opened from a drawer,
  // never needed for the first paint), so plain refs are fine here.
  const mobileForFilters = ref(null)
  const mobileAllFilterValues = ref(null)
  const mobileRequestData = ref(null)
  const mobileMeta = ref(null)
  const mobilePreviewLoading = ref(false)

  // Keep the mobile filter snapshot in sync whenever the main catalog
  // payload changes (including the very first, server-rendered payload).
  watch(
    catalogPayload,
    (payload) => {
      mobileForFilters.value = payload?.for_filters ?? null
      mobileAllFilterValues.value = payload?.all_filter_values ?? null
    },
    { immediate: true },
  )

  async function fetchMobilePreview(url) {
    if (!url) return
    mobilePreviewLoading.value = true
    try {
      const res = await useRequest(url)
      if (res.success && !res?.redirect) {
        mobileRequestData.value = res.data
        mobileMeta.value = res.meta
        mobileForFilters.value = res.for_filters
        mobileAllFilterValues.value = res.all_filter_values
      }
    } catch (e) {
      console.warn('Mobile filter preview failed:', e.message)
    } finally {
      mobilePreviewLoading.value = false
    }
  }

  function resetMobilePreview() {
    mobileForFilters.value = forFilters.value
    mobileAllFilterValues.value = allFilterValues.value
  }

  return {
    requestData,
    meta,
    forFilters,
    allFilterValues,
    mobileForFilters,
    mobileAllFilterValues,
    mobilePreviewLoading,
    loading,
    error,
    refresh: refreshCatalog,
    fetchMobilePreview: (url) => fetchMobilePreview(url ?? mobilePreviewUrl.value),
    resetMobilePreview,
  }
}